/**
 * Friendship Panel Component
 * Coláiste Lurgan Management System - Display and manage student friendships
 */

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useStudentFriendships } from '../../hooks/useStudents';
import { AddFriendDialog } from './AddFriendDialog';
import { RemoveFriendDialog } from './RemoveFriendDialog';
import { Users, UserPlus, X, Loader2, AlertCircle, ArrowUpRight } from 'lucide-react';
import { Tooltip } from '../ui/tooltip';

const MAX_FRIENDS = 2;

function FriendshipCardTitle({ children }) {
  const { t } = useTranslation();
  return (
    <CardTitle className="flex items-center text-sm font-semibold text-foreground">
      <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#F28482]/10 mr-3">
        <Users className="h-4 w-4 text-[#F28482]" />
      </span>
      {t('friendships.title')}
      {children}
    </CardTitle>
  );
}

export function FriendshipPanel({ student, readOnly = false, emptyDescription }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [selectedFriendship, setSelectedFriendship] = useState(null);
  const [selectedFriendName, setSelectedFriendName] = useState('');

  // Fetch friendships for this student
  const { data: friendships, isLoading, error } = useStudentFriendships(student?.uuid);

  // Calculate unique friends (including transitive relationships)
  const friendsData = useMemo(() => {
    if (!friendships || friendships.length === 0) {
      return {
        friends: [],
        friendCount: 0,
        isFull: false,
      };
    }

    const studentUuid = student.uuid;
    const friendMap = new Map(); // uuid -> { name, friendship, isDirect }

    // First pass: collect all unique students from ALL friendships in the group
    const allStudentUuids = new Set();
    friendships.forEach(friendship => {
      allStudentUuids.add(friendship.student1);
      allStudentUuids.add(friendship.student2);
    });

    // Remove current student from the set (we don't want to show them as their own friend)
    allStudentUuids.delete(studentUuid);

    // Second pass: build friend data for each student in the group
    allStudentUuids.forEach(friendUuid => {
      // Check if this friend has a DIRECT friendship with current student
      const directFriendship = friendships.find(f =>
        (f.student1 === studentUuid && f.student2 === friendUuid) ||
        (f.student2 === studentUuid && f.student1 === friendUuid)
      );

      // Get the friend's name from any friendship they're in
      const anyFriendship = friendships.find(f =>
        f.student1 === friendUuid || f.student2 === friendUuid
      );

      if (anyFriendship) {
        const friendName = anyFriendship.student1 === friendUuid
          ? anyFriendship.student1_name
          : anyFriendship.student2_name;

        friendMap.set(friendUuid, {
          uuid: friendUuid,
          name: friendName,
          friendship: directFriendship || anyFriendship, // Use direct friendship if available
          isDirect: !!directFriendship, // True if directly connected, false if transitive
        });
      }
    });

    const friends = Array.from(friendMap.values());
    const friendCount = friends.length;
    const isFull = friendCount >= MAX_FRIENDS;

    return { friends, friendCount, isFull };
  }, [friendships, student]);

  const handleRemoveClick = (friendship, friendName) => {
    setSelectedFriendship(friendship);
    setSelectedFriendName(friendName);
    setRemoveDialogOpen(true);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="bg-muted/40 border-b border-border py-3 px-4">
          <FriendshipCardTitle />
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader className="bg-muted/40 border-b border-border py-3 px-4">
          <FriendshipCardTitle />
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8 text-red-600">
            <AlertCircle className="h-6 w-6 mr-2" />
            <p className="text-sm">{t('friendships.loadError')}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="bg-muted/40 border-b border-border py-3 px-4">
          <div className="flex items-center justify-between">
            <FriendshipCardTitle>
              <Badge variant={friendsData.isFull ? 'destructive' : 'secondary'} className="ml-2">
                {t('friendships.countOfMax', { count: friendsData.friendCount, max: MAX_FRIENDS })}
              </Badge>
            </FriendshipCardTitle>
            {!readOnly && (
              <Button
                size="sm"
                onClick={() => setAddDialogOpen(true)}
                disabled={friendsData.isFull}
              >
                <UserPlus className="h-4 w-4 mr-2" />
                {t('friendships.addFriend')}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-3 pt-4">
          {friendsData.friends.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm font-medium">{t('friendships.noFriends')}</p>
              <p className="text-xs text-muted-foreground mt-1">{emptyDescription || t('friendships.noFriendsDescription')}</p>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                {friendsData.friends.map((friend) => (
                  <div
                    key={friend.uuid}
                    className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/60 transition-colors"
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{friend.name}</p>
                    </div>
                    <div className="flex items-center gap-1 ml-2">
                      <Tooltip content={t('students.goToStudent', 'Go to student')} side="top">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/students/${friend.uuid}`)}
                          className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                          aria-label={t('students.goToStudent', 'Go to student')}
                        >
                          <ArrowUpRight className="h-4 w-4" />
                        </Button>
                      </Tooltip>
                      {!readOnly && friend.isDirect && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveClick(friend.friendship, friend.name)}
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-800"
                          aria-label={t('common.remove')}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Add Friend Dialog */}
      <AddFriendDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        student={student}
        currentFriends={friendships || []}
      />

      {/* Remove Friend Confirmation Dialog */}
      <RemoveFriendDialog
        open={removeDialogOpen}
        onOpenChange={setRemoveDialogOpen}
        friendship={selectedFriendship}
        friendName={selectedFriendName}
        alsoUnlinkedNames={friendsData.friends.filter(f => !f.isDirect).map(f => f.name)}
      />
    </>
  );
}

export default FriendshipPanel;
